/**
 * pages/ProfileHub.js — v3.7.7 Phase 84.31 (unified Profile page).
 *
 * Replaces the previous split between:
 *   • `/settings?section=profile`       (identity — name, DOB, currency)
 *   • `/settings?section=life-profile`  (life & priorities questions)
 *   • `/settings/life-profile`          (deep link into the same)
 *   • `/profile`                        (old Financial DNA drill-down —
 *     preserved as `/financial-dna` for anyone still linking there)
 *
 * The Profile Hub is one URL with four sub-tabs whose active tab is
 * persisted in the query string so links + browser back/forward behave
 * as users expect. Each tab is a self-contained component so we can
 * evolve them (Phases 2-5) without touching the hub.
 *
 *   /profile                → 'identity' (default)
 *   /profile?tab=life       → life & priorities
 *   /profile?tab=location   → location & money
 *   /profile?tab=privacy    → privacy & AI
 *
 * Backward-compat redirect: /settings?section=profile and
 * /settings?section=life-profile still resolve — Settings.js retains a
 * proxy nav item that navigates here instead of rendering embedded.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { User, Sparkles, Globe, Shield } from 'lucide-react';
import usePageTitle from '@/hooks/usePageTitle';
import SectionErrorBoundary from '@/components/SectionErrorBoundary';

import AccountIdentityTab from '@/components/profile/AccountIdentityTab';
import LifePrioritiesTab  from '@/components/profile/LifePrioritiesTab';
import LocationMoneyTab   from '@/components/profile/LocationMoneyTab';
import PrivacyAiTab       from '@/components/profile/PrivacyAiTab';

const TABS = [
  { id: 'identity', label: 'Account & identity',   short: 'Account',  icon: User,
    hint: 'Your name, email, date of birth and how you sign in.',
    node: <AccountIdentityTab /> },
  { id: 'life',     label: 'My life & priorities', short: 'Life',     icon: Sparkles,
    hint: 'Household, work, priorities and preferences that shape your plan.',
    node: <LifePrioritiesTab /> },
  { id: 'location', label: 'Location & money',     short: 'Location', icon: Globe,
    hint: 'Country of residence, tax residency and currency preferences.',
    node: <LocationMoneyTab /> },
  { id: 'privacy',  label: 'Privacy & AI',         short: 'Privacy',  icon: Shield,
    hint: 'AI personalization, notifications, data export and deletion.',
    node: <PrivacyAiTab /> },
];
const VALID = new Set(TABS.map((t) => t.id));

export default function ProfileHub() {
  usePageTitle('Profile');
  const [searchParams, setSearchParams] = useSearchParams();

  const param = searchParams.get('tab');
  const initial = VALID.has(param) ? param : 'identity';
  const [active, setActive] = useState(initial);

  // Reflect ?tab= changes from deep-links / back/forward navigation.
  useEffect(() => {
    const q = searchParams.get('tab');
    if (q && VALID.has(q) && q !== active) setActive(q);
  }, [searchParams, active]);

  const changeTab = (id) => {
    setActive(id);
    const next = new URLSearchParams(searchParams);
    if (id === 'identity') next.delete('tab');
    else                   next.set('tab', id);
    setSearchParams(next, { replace: true });
    // Scroll the top of the panel back into view on mobile.
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { /* ignore */ }
  };

  const entry = useMemo(() => TABS.find((t) => t.id === active) || TABS[0], [active]);

  return (
    <div className="max-w-5xl mx-auto space-y-5" data-testid="profile-hub">
      <header className="px-1">
        <h1 className="font-display text-2xl md:text-3xl font-medium tracking-tight text-charcoal">
          Profile
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          {entry.hint}
        </p>
      </header>

      {/* Tab strip — horizontal on all sizes but wraps neatly on mobile. */}
      <div
        role="tablist"
        aria-label="Profile sections"
        className="flex flex-wrap gap-1 border-b border-border/70 -mx-1 px-1 overflow-x-auto scrollbar-none"
        data-testid="profile-hub-tabs"
        onKeyDown={(e) => {
          // v3.7.11 Phase 84.31 Part 5 — WAI-ARIA tab roving keys.
          // ArrowLeft/Right cycles focus; Home/End jump to endpoints.
          if (!['ArrowLeft','ArrowRight','Home','End'].includes(e.key)) return;
          e.preventDefault();
          const idx = TABS.findIndex((t) => t.id === active);
          let next = idx;
          if (e.key === 'ArrowLeft')  next = (idx - 1 + TABS.length) % TABS.length;
          if (e.key === 'ArrowRight') next = (idx + 1) % TABS.length;
          if (e.key === 'Home')       next = 0;
          if (e.key === 'End')        next = TABS.length - 1;
          const target = TABS[next];
          if (!target) return;
          changeTab(target.id);
          // Move DOM focus so keyboard users see the change.
          const el = document.getElementById(`profile-hub-tab-${target.id}`);
          try { el?.focus(); } catch { /* non-focusable */ }
        }}
      >
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = t.id === active;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`profile-hub-panel-${t.id}`}
              id={`profile-hub-tab-${t.id}`}
              tabIndex={isActive ? 0 : -1}
              onClick={() => changeTab(t.id)}
              className={
                'inline-flex items-center gap-2 px-3.5 py-2.5 text-sm font-medium rounded-t-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ' +
                (isActive
                  ? 'text-charcoal border-b-2 border-primary -mb-px bg-card'
                  : 'text-muted-foreground hover:text-charcoal hover:bg-muted/40 border-b-2 border-transparent -mb-px')
              }
              data-testid={`profile-hub-tab-${t.id}`}
            >
              <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="hidden sm:inline">{t.label}</span>
              <span className="sm:hidden">{t.short}</span>
            </button>
          );
        })}
      </div>

      <section
        role="tabpanel"
        id={`profile-hub-panel-${entry.id}`}
        aria-labelledby={`profile-hub-tab-${entry.id}`}
        data-testid={`profile-hub-panel-${entry.id}`}
        tabIndex={0}
        className="pt-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 rounded-md"
      >
        <SectionErrorBoundary label={entry.label}>
          {entry.node}
        </SectionErrorBoundary>
      </section>
    </div>
  );
}
